import React, { useMemo } from 'react';

import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import processing_activities_messages from 'messages/processing_activities_messages';
import ProcessingActivitiesFilterSelect from 'pages/ProcessingActivities/ProcessingActivitiesPage/_components/ProcessingActivitiesFilterSelect';

import type { AssetRopaReviewFilterOptions, AssetRopaReviewFilters } from '../assetRopaReview.types';
import useStyles from '../ProcessingActivitiesAssetReviewsPage.styles';

type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;

const REVIEW_STATUS_OPTIONS = Object.keys(processing_activities_messages.asset_ropa.review_status) as ReviewStatusMessageKey[];

type Props = {
  filterOptions: AssetRopaReviewFilterOptions;
  filters: AssetRopaReviewFilters;
  onFiltersChange: (filters: AssetRopaReviewFilters) => void;
  onSearch: (value: string) => void;
  searchValue: string;
};

const AssetRopaFiltersToolbar: React.FC<Props> = ({ filterOptions, filters, onFiltersChange, onSearch, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const filterMessages = processing_activities_messages.asset_ropa.filters;

  const reviewStatusOptions = useMemo(
    () =>
      REVIEW_STATUS_OPTIONS.map(status => ({
        value: status,
        label: t(processing_activities_messages.asset_ropa.review_status[status]),
      })),
    [t],
  );

  const assetTypeOptions = useMemo(
    () =>
      filterOptions.assetTypes.map(assetType => ({
        value: String(assetType.id),
        label: assetType.name,
      })),
    [filterOptions.assetTypes],
  );

  const operationalOwnerOptions = useMemo(
    () =>
      filterOptions.operationalOwners.map(owner => ({
        value: String(owner.id),
        label: owner.name,
      })),
    [filterOptions.operationalOwners],
  );

  return (
    <Box className={styles.toolbarSection}>
      <Box className={styles.filtersRow}>
        <div className={styles.searchInputCell}>
          <InputWithDebouncedAction
            action={onSearch}
            formControl={{ size: 'small' }}
            label={t(filterMessages.search_placeholder)}
            value={searchValue}
          />
        </div>

        <ProcessingActivitiesFilterSelect
          allLabel={t(filterMessages.review_status_all)}
          ariaLabel={t(filterMessages.review_status)}
          onChange={reviewStatus => onFiltersChange({ ...filters, reviewStatus: reviewStatus as AssetRopaReviewFilters['reviewStatus'] })}
          options={reviewStatusOptions}
          value={filters.reviewStatus}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={t(filterMessages.asset_type_all)}
          ariaLabel={t(filterMessages.asset_type)}
          onChange={assetTypeId =>
            onFiltersChange({
              ...filters,
              assetTypeId: assetTypeId ? Number(assetTypeId) : '',
            })
          }
          options={assetTypeOptions}
          value={filters.assetTypeId === '' ? '' : String(filters.assetTypeId)}
        />

        <ProcessingActivitiesFilterSelect
          allLabel={t(filterMessages.operational_owner_all)}
          ariaLabel={t(filterMessages.operational_owner)}
          onChange={operationalOwnerMembershipId =>
            onFiltersChange({
              ...filters,
              operationalOwnerMembershipId: operationalOwnerMembershipId ? Number(operationalOwnerMembershipId) : '',
            })
          }
          options={operationalOwnerOptions}
          value={filters.operationalOwnerMembershipId === '' ? '' : String(filters.operationalOwnerMembershipId)}
        />
      </Box>
    </Box>
  );
};

export default AssetRopaFiltersToolbar;
